// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { useState, useId, useRef, useEffect, cloneElement } from 'react';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import { wrapFunction, getFocusReturned, isFocusVisible } from '../utils';
import { TooltipContent } from './TooltipContent';
import { useVirtualElement } from './useVirtualElement';
import { getA11yAttributes } from './getA11yAttributes';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `Tooltip` component is used for providing information about an interface element
 * while user hovers the pointer over the element.
 * @since 0.0.35
 */
const Tooltip = _ref => {
  let {
    title,
    children,
    delay = 550,
    asLabel = false,
    className,
    baseClassName = `${CLS_PREFIX}tooltip`,
    ...props
  } = _ref;
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);
  const [trigger, setTrigger] = useState(null);
  const id = useId();
  const timer = useRef(null);
  const isPointerDown = useRef(false);
  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => () => clearTimer(), []);
  const [virtualElement, createVirtualElement, resetVirtualElement] = useVirtualElement();
  const handlePointerMove = e => {
    createVirtualElement(e);
  };
  const handlePointerEnter = e => {
    if (e.pointerType === 'touch') return;
    createVirtualElement(e);
    setReady(true);
    timer.current = setTimeout(() => handleShow(), delay);
  };
  const handleShow = () => {
    clearTimer();
    setVisible(true);
  };
  const handleHide = () => {
    clearTimer();
    setVisible(false);
    setReady(false);
    resetVirtualElement();
  };
  const handlePointerDown = () => {
    handleHide();
    isPointerDown.current = true;
  };
  const handlePointerUp = () => {
    isPointerDown.current = false;
  };
  const handleFocus = e => {
    if (isPointerDown.current) return;
    if (!isFocusVisible(e.target) && !isFocusVisible(e.currentTarget)) return;
    setReady(true);
    setTrigger(e.currentTarget);
    const delay = getFocusReturned(e.target);
    if (delay) {
      timer.current = setTimeout(() => handleShow(), delay);
    } else {
      handleShow();
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/cloneElement(children, {
      onPointerEnter: wrapFunction(children.props.onPointerEnter, handlePointerEnter),
      onPointerMove: wrapFunction(children.props.onPointerMove, handlePointerMove),
      onPointerLeave: wrapFunction(children.props.onPointerLeave, handleHide),
      onPointerUp: wrapFunction(children.props.onPointerUp, handlePointerUp),
      onPointerDown: wrapFunction(children.props.onPointerDown, handlePointerDown),
      onFocus: wrapFunction(children.props.onFocus, handleFocus),
      onBlur: wrapFunction(children.props.onBlur, handleHide),
      ...getA11yAttributes({
        asLabel,
        title,
        ready,
        id: props.id ?? id
      })
    }), ready && /*#__PURE__*/_jsx(TooltipContent, {
      title: title,
      className: classNames(baseClassName, className),
      ...props,
      id: props.id ?? id,
      visible: visible,
      virtualElement: virtualElement,
      trigger: trigger,
      onEscapeKeyDown: handleHide
    })]
  });
};
export default Tooltip;